import type { AuthResponse, LoginCredentials, RegisterRequest } from "@playarena/shared";
import type { ApiResponse } from "@playarena/shared";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getEnv } from "@/utils/helpers";

import { setCredentials, setTokens, logout } from "../slices/authSlice";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnv()?.VITE_API_URL}/${getEnv()?.VITE_APP_VERSION}/auth`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Auth"],
  endpoints: builder => ({
    login: builder.mutation<ApiResponse<AuthResponse>, LoginCredentials>({
      query: credentials => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update auth state through slice action
            dispatch(setCredentials(data.data));
          }
        } catch (error) {
          console.error("authApi.login error:", error);
        }
      },
      invalidatesTags: ["Auth"],
    }),

    register: builder.mutation<ApiResponse<AuthResponse>, RegisterRequest>({
      query: data => ({
        url: "/register",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update auth state through slice action
            dispatch(setCredentials(data.data));
          }
        } catch (error) {
          console.error("authApi.register error:", error);
        }
      },
      invalidatesTags: ["Auth"],
    }),

    googleAuth: builder.mutation<ApiResponse<AuthResponse>, { token: string }>({
      query: data => ({
        url: "/google",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update auth state through slice action
            dispatch(setCredentials(data.data));
          }
        } catch (error) {
          console.error("authApi.googleAuth error:", error);
        }
      },
      invalidatesTags: ["Auth"],
    }),

    refreshToken: builder.mutation<
      ApiResponse<{
        accessToken: string;
        refreshToken: string;
      }>,
      void
    >({
      query: () => ({
        url: "/refresh-token",
        method: "POST",
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update auth state through slice action
            dispatch(setTokens(data.data));
          }
        } catch (error) {
          console.error("authApi.refreshToken error:", error);
          dispatch(logout());
        }
      },
      invalidatesTags: ["Auth"],
    }),

    forgotPassword: builder.mutation<ApiResponse<null>, { email: string }>({
      query: data => ({
        url: "/forgot-password",
        method: "POST",
        body: data,
      }),
    }),

    resetPassword: builder.mutation<ApiResponse<null>, { token: string; password: string }>({
      query: data => ({
        url: "/reset-password",
        method: "POST",
        body: data,
      }),
    }),

    logout: builder.mutation<ApiResponse<null>, void>({
      query: () => ({
        url: "/logout",
        method: "POST",
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Server logout successful
        } catch (error) {
          console.error("authApi.logout error:", error);
          // Even if server logout fails, local state is already cleared
          console.warn("Server logout failed, but local state cleared");
        } finally {
          // Optimistically clear local state
          dispatch(logout());
          // Clear all RTK Query cache and reset API state
          dispatch(authApi.util.resetApiState());
        }
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useRegisterMutation,
  useGoogleAuthMutation,
  useRefreshTokenMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
} = authApi;
